import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import TextButton from 'LuxKit/Button/TextButton'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import zIndex from 'styles/tools/z-index'
import type { HotelPackageSelectionVariant } from './useHotelPackageSelectionExperiment'

const PreviewPanel = styled(Group)`
  position: fixed;
  inset-inline-start: ${rem(12)};
  bottom: calc(${rem(12)} + ${props => props.theme.layout.footerFloatingOffset} + env(safe-area-inset-bottom, 0px));
  z-index: ${zIndex.floatingPanel};
  padding: ${rem(12)};
  max-width: calc(100vw - ${rem(24)});
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.flat.small};

  ${mediaQueryUp.tablet} {
    // Keep the existing offer assistant launcher usable below the preview panel.
    bottom: max(${rem(96)}, calc(${rem(16)} + ${props => props.theme.layout.footerFloatingOffset}));
    inset-inline-start: ${rem(16)};
  }
`

interface Props {
  variant: HotelPackageSelectionVariant
  onChange: (variant: HotelPackageSelectionVariant) => void
  product: 'SPP' | 'Flash'
}

/** Package-selection preview control; mount only when the experiment hook permits preview. */
function HotelPackageSelectionPreview({ variant, onChange, product }: Props) {
  const intl = useIntl()
  const title = useMemo(() => intl.formatMessage({
    id: 'Y-8EAOfpm6pwpE9U3AZF',
    defaultMessage: '{product} preview — only for le staff',
    description: 'Title of the employee-only hotel package selection preview switcher including its product name',
  }, { product }), [intl, product])
  const selectControl = useCallback(() => onChange('control'), [onChange])
  const selectOptionD = useCallback(() => onChange('option-d'), [onChange])

  return <PreviewPanel
    direction="vertical"
    gap={8}
    role="group"
    aria-label={intl.formatMessage({
      id: 'l4R0zuYjQK6LmCBkxLml',
      defaultMessage: 'Package selection preview',
      description: 'Accessible name for the hotel package selection preview variant switcher',
    })}
  >
    <Caption variant="small">{title}</Caption>
    <Group direction="horizontal" gap={8}>
      <TextButton kind={variant === 'control' ? 'primary' : 'secondary'} size="small" aria-pressed={variant === 'control'} onClick={selectControl}>
        {intl.formatMessage({
          id: 'nxdPJkyqan8zdywe7rgW',
          defaultMessage: 'Control',
          description: 'Button to preview the existing control hotel package selection experience',
        })}
      </TextButton>
      <TextButton kind={variant === 'option-d' ? 'primary' : 'secondary'} size="small" aria-pressed={variant === 'option-d'} onClick={selectOptionD}>
        {intl.formatMessage({
          id: 'AK0ZimNqttZQq2GzCydM',
          defaultMessage: 'Option D',
          description: 'Button to preview the Option D hotel package selection experience',
        })}
      </TextButton>
    </Group>
  </PreviewPanel>
}

export default HotelPackageSelectionPreview
